import type {BoardFocusAxis, BoardFocusStep} from './types'

export type BoardFocusMove = {axis: BoardFocusAxis; step: BoardFocusStep}

/**
 * Which of the board's two modifiers is held.
 *
 * Two rather than one, because no single modifier is free for both jobs on every platform
 * the board ships to. `Shift` belongs to the selection gesture on a card. `Alt` with a
 * horizontal arrow is browser history and `Alt` with `Home` is the browser's home page, so
 * `Alt` is only free on the vertical arrows. `Control` with an arrow is Mission Control and
 * Spaces on macOS, so `Control` is only free on `Home` and `End`, which is also where the
 * ARIA grid pattern puts it: "Control + Home moves focus to the first cell in the first
 * row". `Meta` is free nowhere, being window management on Windows and document and line
 * gestures on macOS.
 */
export type BoardFocusModifiers = {
  /** `Alt`, which turns the vertical keys into a jump between swimlanes. */
  alt: boolean
  /** `Control`, which turns `Home` and `End` into the ends of the column axis. */
  control: boolean
}

/**
 * The keys the board moves focus for, and what each one means.
 *
 * Kept apart from the reducer, which is given a direction along an axis and never sees an
 * event. That is the split that lets the same reducer be driven by a key, by a control,
 * or by a test, and it is why there is no key name anywhere in the movement logic.
 *
 * Ten combinations over six keys, which is a roving tabindex a reader can get everywhere
 * with: a composite widget whose one tab stop cannot be moved is worse to use than the
 * several stops it replaced. The three paging ghosts and the row headers are positions
 * this map already reaches, because each one sits at the end of an axis this map already
 * moves along.
 *
 * Left and right follow the writing direction, because the column axis is laid out with
 * logical properties and a reader in a right-to-left locale sees the first column on the
 * right. Up and down do not, because block direction is the same either way here.
 *
 * `Home` and `End` are the ends of the cell rather than of the board. A person pressing
 * them is asking about the list they are reading. With `Control` they are the ends of the
 * row, which is a question about the columns.
 *
 * A modifier this board has no use for on a given key is left alone rather than swallowed,
 * because it is somebody else's: see {@link BoardFocusModifiers} for whose.
 */
export function boardFocusMoveForKey(
  key: string,
  {alt, control}: BoardFocusModifiers,
  isRightToLeft: boolean,
): BoardFocusMove | null {
  switch (key) {
    case 'ArrowLeft':
      if (alt || control) return null

      return {axis: 'column', step: isRightToLeft ? 'next' : 'previous'}
    case 'ArrowRight':
      if (alt || control) return null

      return {axis: 'column', step: isRightToLeft ? 'previous' : 'next'}
    case 'ArrowUp':
      if (control) return null

      return {axis: alt ? 'row' : 'card', step: 'previous'}
    case 'ArrowDown':
      if (control) return null

      return {axis: alt ? 'row' : 'card', step: 'next'}
    case 'Home':
      if (alt) return null

      return {axis: control ? 'column' : 'card', step: 'first'}
    case 'End':
      if (alt) return null

      return {axis: control ? 'column' : 'card', step: 'last'}
    default:
      return null
  }
}

/** Whether the two keys that cross the board need reading against the writing direction. */
export function isAcrossTheBoard(key: string): boolean {
  return key === 'ArrowLeft' || key === 'ArrowRight'
}

/**
 * Composite widgets a caller can draw inside a card, which run their own interaction.
 *
 * Roles rather than tags, because a caller's combobox or slider is usually a `div`
 * carrying one.
 *
 * Read by both guards that ask whether an event was the card's own: the arrow keys
 * below, and the pointer gesture in `selection/use-board-card-selection.ts`. One list,
 * because a widget that owns its arrow keys owns its clicks too, and two would drift.
 */
export const OWN_WIDGET_ROLES = new Set([
  'combobox',
  'grid',
  'listbox',
  'menu',
  'menubar',
  'radiogroup',
  'slider',
  'spinbutton',
  'tablist',
  'tree',
  'treegrid',
])

/**
 * Whether this element answers the key itself, so the board must leave it alone. A caller
 * draws whatever it likes inside a card, so the board asks rather than assuming a card
 * holds only text.
 *
 * `defaultPrevented` catches the rest, but only for a widget that handles the key in
 * script: a media element seeks with the arrows as a *default action*, so nothing reports
 * it and it has to be named here.
 */
export function isOwnKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false

  if (target.isContentEditable) return true

  const tagName = target.tagName
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') return true
  if ((tagName === 'VIDEO' || tagName === 'AUDIO') && target.hasAttribute('controls')) return true

  const role = target.getAttribute('role')

  return role !== null && OWN_WIDGET_ROLES.has(role)
}
